import { useEffect, useState, useCallback } from "react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ProductShippingPanel } from "@/components/admin/ProductShippingPanel";

const Sec = ({ title, children, testid }) => <section className="rounded-xl border bg-white p-4 space-y-3" data-testid={testid}><h3 className="font-bold text-steel">{title}</h3>{children}</section>;
const sel = "h-9 rounded-md border bg-white px-2 text-sm";
const err = (e) => toast.error(e.response?.data?.detail || "Failed");
const EMPTY_ZONE = { name: "", postcodes: "", territories: "", mode: "quote", rate: "", economy_rate: "", free_allowed: false, message: "" };

export function ZonesPanel() {
  const [zones, setZones] = useState([]);
  const [z, setZ] = useState(EMPTY_ZONE);
  const load = useCallback(() => api.get("/admin/shipping/zones").then((r) => setZones(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const split = (v) => (Array.isArray(v) ? v : String(v || "").split(",").map((x) => x.trim()).filter(Boolean));
  const save = async () => { try { await api.post("/admin/shipping/zones", { ...z, postcodes: split(z.postcodes), territories: split(z.territories) }); toast.success("Zone saved"); setZ(EMPTY_ZONE); load(); } catch (e) { err(e); } };
  return (
    <Sec title="Special delivery zones" testid="ship-zones">
      <div className="divide-y rounded-lg border text-sm">
        {zones.map((x) => (
          <div key={x.id} className="p-2 flex items-center gap-2">
            <button className="flex-1 text-left" onClick={() => setZ({ ...x, postcodes: x.postcodes.join(", "), territories: x.territories.join(", "), rate: x.rate ?? "", economy_rate: x.economy_rate ?? "" })} data-testid={`ship-zone-${x.name}`}>
              <b>{x.name}</b> <span className="text-muted-foreground">· {x.postcodes.join(", ")} · {x.mode === "rate" ? `rate ${money(x.rate || 0)}` : x.mode}</span></button>
            <button onClick={async () => { await api.delete(`/admin/shipping/zones/${x.id}`); load(); }} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
          </div>))}
      </div>
      <div className="grid sm:grid-cols-4 gap-2">
        <Input placeholder="Zone name" value={z.name} onChange={(e) => setZ({ ...z, name: e.target.value })} data-testid="ship-zone-name" />
        <Input placeholder="Postcodes (comma)" value={z.postcodes} onChange={(e) => setZ({ ...z, postcodes: e.target.value })} data-testid="ship-zone-postcodes" />
        <select value={z.mode} onChange={(e) => setZ({ ...z, mode: e.target.value })} className={sel} data-testid="ship-zone-mode"><option value="quote">Manual quotation</option><option value="rate">Custom rate</option><option value="blocked">No online delivery</option></select>
        <select value={z.island || ""} onChange={(e) => setZ({ ...z, island: e.target.value })} className={sel} data-testid="ship-zone-island"><option value="">Island…</option><option value="north">North Island</option><option value="south">South Island</option></select>
        <Input type="number" placeholder="Rate $" value={z.rate} onChange={(e) => setZ({ ...z, rate: e.target.value })} data-testid="ship-zone-rate" />
        <Input placeholder="Territories (Addy, comma)" value={z.territories} onChange={(e) => setZ({ ...z, territories: e.target.value })} />
        <Input type="number" placeholder="Economy rate $" value={z.economy_rate} onChange={(e) => setZ({ ...z, economy_rate: e.target.value })} />
        <Input placeholder="Customer message" value={z.message} onChange={(e) => setZ({ ...z, message: e.target.value })} />
        <Button onClick={save} disabled={!z.name} data-testid="ship-zone-save">{z.id ? "Update zone" : "Add zone"}</Button>
      </div>
    </Sec>
  );
}

export function PostcodesPanel() {
  const [rows, setRows] = useState([]);
  const [r, setR] = useState({ postcode: "", island: "north", rural: false, note: "" });
  const load = useCallback(() => api.get("/admin/shipping/postcodes").then((x) => setRows(x.data)), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => { try { await api.post("/admin/shipping/postcodes", r); setR({ ...r, postcode: "", note: "" }); load(); } catch (e) { err(e); } };
  return (
    <Sec title="Postcode overrides (used when the address isn't Addy-verified)" testid="ship-postcodes">
      <div className="flex flex-wrap gap-2">
        <Input placeholder="0000" value={r.postcode} onChange={(e) => setR({ ...r, postcode: e.target.value })} className="w-24" data-testid="ship-pc-input" />
        <select value={r.island} onChange={(e) => setR({ ...r, island: e.target.value })} className={sel}><option value="north">North Island</option><option value="south">South Island</option></select>
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={r.rural} onChange={(e) => setR({ ...r, rural: e.target.checked })} /> Rural</label>
        <Input placeholder="Note" value={r.note} onChange={(e) => setR({ ...r, note: e.target.value })} className="w-48" />
        <Button size="sm" onClick={save} data-testid="ship-pc-save">Save postcode</Button>
      </div>
      {rows.length > 0 && <div className="text-xs flex flex-wrap gap-2">{rows.map((x) => (
        <span key={x.postcode} className="px-2 py-1 rounded bg-slate-100 flex items-center gap-1">{x.postcode} · {x.island}{x.rural ? " · rural" : ""}
          <button onClick={async () => { await api.delete(`/admin/shipping/postcodes/${x.postcode}`); load(); }}><Trash2 size={11} /></button></span>))}</div>}
    </Sec>
  );
}

export function BulkAssignPanel() {
  const [by, setBy] = useState("skus");
  const [val, setVal] = useState("");
  const [ship, setShip] = useState({ class: "small", economy: true, packaging: "satchel" });
  const [picked, setPicked] = useState([]);
  const apply = async () => {
    const body = by === "name" ? { shipping: ship, skus: picked.map((p) => p.sku) }
      : { shipping: ship, [by]: by === "skus" ? val.split(/[\s,]+/).filter(Boolean) : val.trim() };
    try { const r = await api.post("/admin/shipping/assign", body); toast.success(`${r.data.updated} of ${r.data.matched} product(s) updated`); } catch (e) { err(e); }
  };
  return (
    <Sec title="Bulk assign product shipping" testid="ship-bulk">
      <div className="flex flex-wrap gap-2">
        <select value={by} onChange={(e) => setBy(e.target.value)} className={sel} data-testid="ship-bulk-by"><option value="skus">SKU list</option><option value="name">Product name</option><option value="category">Category name</option><option value="brand">Brand name</option></select>
        {by === "name" ? <ProductPicker picked={picked} setPicked={setPicked} />
          : <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={by === "skus" ? "SKU1, SKU2 …" : "Exact name"} className="flex-1 min-w-[200px]" data-testid="ship-bulk-value" />}
      </div>
      <ProductShippingPanel value={ship} onChange={setShip} />
      <Button onClick={apply} disabled={by === "name" ? !picked.length : !val.trim()} className="bg-steel text-white" data-testid="ship-bulk-apply">Apply to matching products</Button>
      <ShippingCsv />
    </Sec>
  );
}

function ProductPicker({ picked, setPicked }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/admin/products", { params: { q: q.trim(), limit: 15 } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);
  const add = (p) => { if (!picked.some((x) => x.id === p.id)) setPicked([...picked, { id: p.id, sku: p.sku, name: p.name }]); setQ(""); setRes([]); };
  return (
    <div className="flex-1 min-w-[260px] relative" data-testid="ship-bulk-product-picker">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Start typing a product name or SKU…" data-testid="ship-bulk-product-search" />
      {res.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-md border bg-white shadow-lg" data-testid="ship-bulk-product-results">
          {res.map((p) => (
            <button type="button" key={p.id} onClick={() => add(p)} className="block w-full text-left px-3 py-2 text-sm hover:bg-slate-100" data-testid={`ship-bulk-product-opt-${p.sku}`}>
              <span className="font-medium">{p.name}</span> <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
              <span className="text-xs text-muted-foreground"> · {p.shipping?.class || "standard"}</span>
            </button>))}
        </div>)}
      {picked.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="ship-bulk-picked">
          {picked.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 text-xs bg-slate-100 border rounded-full px-2 py-1">
              {p.name.slice(0, 40)} <span className="font-mono text-muted-foreground">{p.sku}</span>
              <button type="button" onClick={() => setPicked(picked.filter((x) => x.id !== p.id))} className="text-red-600 font-bold" data-testid={`ship-bulk-unpick-${p.sku}`}>×</button>
            </span>))}
        </div>)}
    </div>
  );
}

function ShippingCsv() {
  const download = async () => {
    const r = await api.get("/admin/shipping/export.csv", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "product-shipping.csv"; a.click();
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/shipping/import", fd); toast.success(`${r.data.updated} updated · ${r.data.not_found} SKUs not found`); } catch (er) { err(er); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center pt-2 border-t text-sm" data-testid="ship-csv">
      <Button size="sm" variant="outline" onClick={download} data-testid="ship-csv-export">Export shipping CSV</Button>
      <label><input type="file" accept=".csv" className="hidden" onChange={upload} data-testid="ship-csv-import" /><span className="px-3 py-1.5 rounded-md border cursor-pointer">Import shipping CSV</span></label>
      <span className="text-xs text-muted-foreground">Columns by SKU; blank cells are left unchanged. Supplier imports never change shipping.</span>
    </div>
  );
}

export function CalculatorPanel() {
  const [f, setF] = useState({ region: "Auckland", postcode: "", line1: "", skus: "", qty: 1, subtotal: "" });
  const [res, setRes] = useState(null);
  const run = async () => {
    try {
      const items = f.skus.split(/[\s,]+/).filter(Boolean).map((s) => ({ sku: s, qty: Number(f.qty) || 1 }));
      const r = await api.post("/admin/shipping/calculator", { items, address: { region: f.region, postcode: f.postcode, line1: f.line1 }, subtotal_override: f.subtotal });
      setRes(r.data);
    } catch (e) { err(e); }
  };
  return (
    <Sec title="Shipping calculator (test)" testid="ship-calc">
      <div className="grid sm:grid-cols-6 gap-2">
        <Input placeholder="Region" value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} data-testid="ship-calc-region" />
        <Input placeholder="Postcode" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} data-testid="ship-calc-postcode" />
        <Input placeholder="Address (RD 2 = rural)" value={f.line1} onChange={(e) => setF({ ...f, line1: e.target.value })} />
        <Input placeholder="SKU(s)" value={f.skus} onChange={(e) => setF({ ...f, skus: e.target.value })} data-testid="ship-calc-skus" />
        <Input type="number" placeholder="Qty" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="ship-calc-qty" />
        <Button onClick={run} data-testid="ship-calc-run">Calculate</Button>
      </div>
      {res && (
        <div className="text-sm rounded-lg border p-3 space-y-1" data-testid="ship-calc-result">
          <p><b>{res.status === "final" ? money(res.shipping) : res.status === "quote_pending" ? `${money(res.shipping)} + freight quote` : "Needs address confirmation"}</b> · {res.method} · {res.zone || res.island || "?"}{res.rural ? " · rural" : ""}</p>
          <p className="text-xs text-muted-foreground">Address: {res.address_reason} · Subtotal {money(res.subtotal)} (eligible {money(res.eligible_subtotal || 0)})</p>
          {res.breakdown.map((b, i) => <p key={i} className="text-xs">{b.label}: {b.pending ? "quote" : money(b.amount)}</p>)}
          {res.notes.map((n, i) => <p key={i} className="text-xs text-amber-700">{n}</p>)}
        </div>
      )}
    </Sec>
  );
}

export function AuditPanel() {
  const [rows, setRows] = useState(null);
  return (
    <Sec title="Shipping change audit log" testid="ship-audit">
      <Button size="sm" variant="outline" onClick={() => api.get("/admin/shipping/audit").then((r) => setRows(r.data))} data-testid="ship-audit-load">Show recent changes</Button>
      {rows && <div className="text-xs divide-y rounded-lg border max-h-60 overflow-y-auto">{rows.map((r) => <p key={r.id} className="p-2">{new Date(r.at).toLocaleString()} · {r.by} · {r.kind}</p>)}</div>}
    </Sec>
  );
}
